import type { PosCartItem, PosCartState } from '@/types/billing';
import type { ProductResponse } from '@/types/product';
import type { PosProductScanResponse } from '@/types/storeProduct';

export function calcLineTotal(
  quantity: number,
  unitPrice: number,
  taxPercentage: number,
  discountAmount: number,
  gstInclusive: boolean,
): number {
  const subtotal = quantity * unitPrice;
  const afterDiscount = subtotal - discountAmount;
  if (gstInclusive) return Math.max(0, Number(afterDiscount.toFixed(2)));
  const tax = afterDiscount * (taxPercentage / 100);
  return Math.max(0, Number((afterDiscount + tax).toFixed(2)));
}

export function scanToCartItem(scan: PosProductScanResponse, qty = 1, gstInclusive = true): PosCartItem {
  const unitPrice = Number(scan.sellingPrice);
  const taxPercentage = Number(scan.taxPercentage ?? 0);
  const discountAmount = 0;
  return {
    productId: scan.productId,
    name: scan.name,
    sku: scan.sku,
    barcode: scan.storeBarcode ?? scan.globalBarcode,
    quantity: qty,
    unitPrice,
    taxPercentage,
    discountAmount,
    lineTotal: calcLineTotal(qty, unitPrice, taxPercentage, discountAmount, gstInclusive),
  };
}

export function productToCartItem(product: ProductResponse, qty = 1, gstInclusive = true): PosCartItem {
  const unitPrice = Number(product.sellingPrice);
  const taxPercentage = Number(product.taxPercentage ?? 0);
  const discountAmount = 0;
  const imageUrl =
    product.imageUrl ??
    product.images?.find((img) => img.primary)?.imageUrl ??
    product.images?.[0]?.imageUrl;

  return {
    productId: product.id,
    name: product.name,
    sku: product.sku,
    barcode: product.barcode,
    imageUrl,
    quantity: qty,
    unitPrice,
    taxPercentage,
    discountAmount,
    lineTotal: calcLineTotal(qty, unitPrice, taxPercentage, discountAmount, gstInclusive),
  };
}

export function cartTotals(cart: PosCartState) {
  const subtotal = cart.items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
  const lineDiscounts = cart.items.reduce((s, i) => s + i.discountAmount, 0);
  const itemsTotal = cart.items.reduce((s, i) => s + i.lineTotal, 0);
  const billDiscount = cart.discountAmount;
  const total = Math.max(0, itemsTotal - billDiscount);
  return { subtotal, lineDiscounts, itemsTotal, billDiscount, total };
}

export function serializeCart(cart: PosCartState): string {
  return JSON.stringify(cart);
}

export function deserializeCart(data: string): PosCartState | null {
  try {
    return JSON.parse(data) as PosCartState;
  } catch {
    return null;
  }
}
